import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const GameContext = createContext(null);

const INITIAL_STATE = {
  username: '',
  xp: 0,
  level: 1,
  completedTopics: [],
  unlockedAchievements: [],
  quizHistory: [],
  streak: 0,
  totalCorrect: 0,
  totalAnswered: 0,
};

const XP_PER_LEVEL = 120;

function calcLevel(xp) {
  return Math.floor(xp / XP_PER_LEVEL) + 1;
}

function calcXpProgress(xp) {
  return xp % XP_PER_LEVEL;
}

export function GameProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem('learn-ngk-state');
      return saved ? { ...INITIAL_STATE, ...JSON.parse(saved) } : INITIAL_STATE;
    } catch {
      return INITIAL_STATE;
    }
  });

  const [notification, setNotification] = useState(null);

  useEffect(() => {
    localStorage.setItem('learn-ngk-state', JSON.stringify(state));
  }, [state]);

  const notify = useCallback((msg, type = 'success') => {
    setNotification({ msg, type, id: Date.now() });
    setTimeout(() => setNotification(null), 3000);
  }, []);

  const setUsername = useCallback((username) => {
    setState(s => ({ ...s, username }));
  }, []);

  const addXp = useCallback((amount, source = '') => {
    setState(s => {
      const newXp = s.xp + amount;
      const newLevel = calcLevel(newXp);
      const leveledUp = newLevel > s.level;
      if (leveledUp) {
        setTimeout(() => notify(`Level Up! You reached Level ${newLevel}!`, 'levelup'), 100);
      }
      return { ...s, xp: newXp, level: newLevel };
    });
  }, [notify]);

  const completeQuiz = useCallback((topicId, result, answers) => {
    setState(s => {
      const newHistory = [
        { topicId, ...result, date: new Date().toISOString() },
        ...s.quizHistory.slice(0, 19),
      ];
      const newCorrect = s.totalCorrect + result.correct;
      const newAnswered = s.totalAnswered + result.total;

      let newCompletedTopics = s.completedTopics;
      if (result.percentage >= 60 && !s.completedTopics.includes(topicId)) {
        newCompletedTopics = [...s.completedTopics, topicId];
      }

      // Check streak
      let newStreak = s.streak;
      const allCorrect = answers && answers.every(a => a.is_correct);
      if (allCorrect) newStreak += result.correct;
      else newStreak = 0;

      return {
        ...s,
        quizHistory: newHistory,
        totalCorrect: newCorrect,
        totalAnswered: newAnswered,
        completedTopics: newCompletedTopics,
        streak: newStreak,
      };
    });
  }, []);

  const unlockAchievement = useCallback((achievementId, title, xpReward) => {
    setState(s => {
      if (s.unlockedAchievements.includes(achievementId)) return s;
      setTimeout(() => notify(`Achievement Unlocked: ${title} +${xpReward} XP`, 'achievement'), 200);
      return {
        ...s,
        unlockedAchievements: [...s.unlockedAchievements, achievementId],
        xp: s.xp + xpReward,
        level: calcLevel(s.xp + xpReward),
      };
    });
  }, [notify]);

  const checkAchievements = useCallback((achievements) => {
    if (!achievements) return;
    setState(s => {
      const earned = [];
      const allTopics = ['hardware', 'os', 'networking', 'security', 'software'];

      achievements.forEach(a => {
        if (s.unlockedAchievements.includes(a.id)) return;
        let unlock = false;
        if (a.id === 'first_quiz' && s.quizHistory.length >= 1) unlock = true;
        if (a.id === 'perfect_score' && s.quizHistory.some(h => h.percentage === 100)) unlock = true;
        if (a.id === 'streak_5' && s.streak >= 5) unlock = true;
        if (a.id === 'streak_10' && s.streak >= 10) unlock = true;
        if (a.id === 'hardware_master' && s.completedTopics.includes('hardware')) unlock = true;
        if (a.id === 'os_master' && s.completedTopics.includes('os')) unlock = true;
        if (a.id === 'network_master' && s.completedTopics.includes('networking')) unlock = true;
        if (a.id === 'security_master' && s.completedTopics.includes('security')) unlock = true;
        if (a.id === 'software_master' && s.completedTopics.includes('software')) unlock = true;
        if (a.id === 'all_topics' && allTopics.every(t => s.completedTopics.includes(t))) unlock = true;
        if (a.id === 'level_5' && s.level >= 5) unlock = true;
        if (a.id === 'level_10' && s.level >= 10) unlock = true;
        if (unlock) earned.push(a);
      });

      if (earned.length === 0) return s;

      const bonusXp = earned.reduce((sum, a) => sum + a.xp_reward, 0);
      const newXp = s.xp + bonusXp;
      earned.forEach((a, i) => {
        setTimeout(() => {
          // notification handled externally to avoid stale closure issues
        }, i * 600);
      });

      return {
        ...s,
        unlockedAchievements: [...s.unlockedAchievements, ...earned.map(a => a.id)],
        xp: newXp,
        level: calcLevel(newXp),
      };
    });
  }, []);

  const resetProgress = useCallback(() => {
    setState(s => ({ ...INITIAL_STATE, username: s.username }));
  }, []);

  const value = {
    ...state,
    xpProgress: calcXpProgress(state.xp),
    xpForNextLevel: XP_PER_LEVEL,
    notification,
    setUsername,
    addXp,
    completeQuiz,
    unlockAchievement,
    checkAchievements,
    resetProgress,
    notify,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside GameProvider');
  return ctx;
}
